import { CREATE_RUNESTONE_MUTATION } from "@/graphql/mutations/forgeMutations";
import { useMutation } from "@apollo/client";
import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { SketchPicker } from "react-color";
import { useNavigate } from "react-router-dom";

interface RunestoneEditorProps {
    svgContent: string;
    runeName: string;
}

type RuneClass = "stoneface" | "bindrune" | "border" | "edge";

interface RuneColors {
    stoneface: string;
    bindrune: string;
    border: string;
    edge: string;
}

const RUNE_CLASS_LABELS: Record<RuneClass, string> = {
    stoneface: "Stoneface",
    bindrune: "Bindrune",
    border: "Border",
    edge: "Edge",
};

const DEFAULT_COLOR = "#ffffff";

const MissingPaintModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
}> = ({ isOpen, onClose }) => (
    <Transition show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>
            <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <div className="bg-brand_charcoal/80 fixed inset-0 transition-opacity" />
            </Transition.Child>
            <div className="fixed inset-0 z-50 overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 text-center">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <Dialog.Panel className="bg-brand_charcoal border-brand relative z-50 transform overflow-hidden rounded-lg border-4 p-6 text-left shadow-xl transition-all sm:max-w-sm">
                            <Dialog.Title className="text-brand mb-4 text-center text-2xl font-bold">
                                Missing Paint!
                            </Dialog.Title>
                            <div className="flex flex-col items-center">
                                <p className="text-brand text-center text-lg">
                                    Sorry, your runestone seems to be missing
                                    some paint! Please colour all four zones
                                    before submitting. Skál!
                                </p>
                            </div>
                            <div className="mt-6 flex justify-end">
                                <button
                                    onClick={onClose}
                                    className="rounded bg-gray-500 px-4 py-2 text-white hover:bg-gray-600"
                                >
                                    OK
                                </button>
                            </div>
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </div>
        </Dialog>
    </Transition>
);

const ConfirmCarveModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
}> = ({ isOpen, onClose, onConfirm }) => (
    <Transition show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>
            <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <div className="bg-brand_charcoal/80 fixed inset-0 transition-opacity" />
            </Transition.Child>
            <div className="fixed inset-0 z-50 overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 text-center">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <Dialog.Panel className="bg-brand_charcoal border-brand relative z-50 transform overflow-hidden rounded-lg border-4 p-6 text-left shadow-xl transition-all sm:max-w-sm">
                            <Dialog.Title className="text-brand mb-4 text-center text-2xl font-bold">
                                Carve the Stone?
                            </Dialog.Title>
                            <div className="flex flex-col items-center">
                                <p className="text-brand text-center text-lg">
                                    You are about to carve your runestone. This
                                    action is final — double check your colours
                                    before confirming. Skál!
                                </p>
                            </div>
                            <div className="mt-6 flex justify-end space-x-2">
                                <button
                                    onClick={onClose}
                                    className="rounded bg-gray-500 px-4 py-2 text-white hover:bg-gray-600"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={onConfirm}
                                    className="rounded bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700"
                                >
                                    Confirm
                                </button>
                            </div>
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </div>
        </Dialog>
    </Transition>
);

const RunestoneEditor: React.FC<RunestoneEditorProps> = ({
    svgContent,
    runeName,
}) => {
    const [colors, setColors] = useState<RuneColors>({
        stoneface: DEFAULT_COLOR,
        bindrune: DEFAULT_COLOR,
        border: DEFAULT_COLOR,
        edge: DEFAULT_COLOR,
    });
    const [activeClass, setActiveClass] = useState<RuneClass>("stoneface");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [isMissingPaintOpen, setIsMissingPaintOpen] = useState(false);
    const svgContainerRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    const [createRunestone, { loading }] = useMutation(
        CREATE_RUNESTONE_MUTATION,
        {
            onCompleted: (data) => {
                if (data.createRunestone.success) {
                    navigate("/dashboard/my-account");
                } else {
                    setErrorMessage(
                        data.createRunestone.message || "An error occurred.",
                    );
                }
            },
            onError: (error) => {
                setErrorMessage(
                    error.message || "An unexpected error occurred.",
                );
            },
        },
    );

    // Render SVG and apply colours whenever svgContent or colours change
    useEffect(() => {
        if (!svgContainerRef.current || !svgContent) return;
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(svgContent, "image/svg+xml");

        const classNames: RuneClass[] = [
            "stoneface",
            "bindrune",
            "border",
            "edge",
        ];
        classNames.forEach((cls) => {
            svgDoc.querySelectorAll(`.${cls}`).forEach((el) => {
                el.setAttribute("fill", colors[cls]);
            });
        });

        svgContainerRef.current.innerHTML = "";
        svgContainerRef.current.appendChild(svgDoc.documentElement);
    }, [svgContent, colors]);

    const handleColorChange = (color: { hex: string }) => {
        setColors((prev) => ({ ...prev, [activeClass]: color.hex }));
    };

    const getCurrentSvgString = (): string => {
        const svgEl = svgContainerRef.current?.querySelector("svg");
        if (!svgEl) return svgContent;
        return new XMLSerializer().serializeToString(svgEl);
    };

    const handleSubmitClick = () => {
        const allDefault = (Object.keys(colors) as RuneClass[]).every(
            (cls) => colors[cls].toLowerCase() === DEFAULT_COLOR,
        );
        if (allDefault) {
            setIsMissingPaintOpen(true);
            return;
        }
        setIsConfirmOpen(true);
    };

    const handleConfirm = () => {
        setIsConfirmOpen(false);
        const currentSvg = getCurrentSvgString();
        if (!currentSvg) {
            setErrorMessage("SVG content is missing.");
            return;
        }
        try {
            const imageData = `data:image/svg+xml;base64,${btoa(currentSvg)}`;
            createRunestone({ variables: { imageData, runeName } });
        } catch {
            setErrorMessage("Failed to encode the SVG. Please try again.");
        }
    };

    return (
        <div className="mt-20 grid grid-cols-5">
            <div className="border-brand bg-brand_charcoal bg-opacity-70 col-span-3 col-start-2 grid grid-cols-2 gap-4 rounded-lg border-4 p-10">
                {/* SVG preview */}
                <div
                    ref={svgContainerRef}
                    className="flex items-center justify-center"
                />

                {/* Colour controls */}
                <div className="flex flex-col items-center justify-start space-y-4 pt-4">
                    {/* Zone selector tabs */}
                    <div className="flex w-full flex-wrap justify-center gap-2">
                        {(Object.keys(RUNE_CLASS_LABELS) as RuneClass[]).map(
                            (cls) => (
                                <button
                                    key={cls}
                                    onClick={() => setActiveClass(cls)}
                                    className={`rounded px-3 py-1 text-sm font-semibold transition-colors ${
                                        activeClass === cls
                                            ? "bg-indigo-600 text-white"
                                            : "bg-gray-600 text-gray-200 hover:bg-gray-500"
                                    }`}
                                >
                                    {RUNE_CLASS_LABELS[cls]}
                                </button>
                            ),
                        )}
                    </div>

                    {/* Current zone colour swatch */}
                    <div
                        className="h-10 w-10 rounded-full border-2 border-gray-300"
                        style={{ backgroundColor: colors[activeClass] }}
                        title={`${RUNE_CLASS_LABELS[activeClass]}: ${colors[activeClass]}`}
                    />

                    <SketchPicker
                        color={colors[activeClass]}
                        onChangeComplete={handleColorChange}
                        width="85%"
                    />

                    {/* Zone summary swatches */}
                    <div className="mt-2 flex gap-3">
                        {(Object.keys(RUNE_CLASS_LABELS) as RuneClass[]).map(
                            (cls) => (
                                <div
                                    key={cls}
                                    className={`h-6 w-6 cursor-pointer rounded-full border-2 ${activeClass === cls ? "border-indigo-400" : "border-gray-400"}`}
                                    style={{ backgroundColor: colors[cls] }}
                                    title={RUNE_CLASS_LABELS[cls]}
                                    onClick={() => setActiveClass(cls)}
                                />
                            ),
                        )}
                    </div>
                </div>

                {/* Error */}
                {errorMessage && (
                    <div className="col-span-2 mb-4 rounded-sm bg-red-500 p-4 text-white">
                        <p>{errorMessage}</p>
                    </div>
                )}

                {/* Action buttons */}
                <div className="col-span-2 flex items-center justify-center space-x-4">
                    <button
                        onClick={() => navigate("/runestone-carving")}
                        className="rounded-sm bg-gray-500 px-4 py-2 text-white hover:bg-gray-600"
                    >
                        Back
                    </button>
                    <button
                        onClick={() =>
                            setColors({
                                stoneface: DEFAULT_COLOR,
                                bindrune: DEFAULT_COLOR,
                                border: DEFAULT_COLOR,
                                edge: DEFAULT_COLOR,
                            })
                        }
                        className="rounded-sm bg-red-500 px-4 py-2 text-white hover:bg-red-600"
                    >
                        Reset
                    </button>
                    <button
                        onClick={handleSubmitClick}
                        disabled={loading}
                        className={`rounded px-4 py-2 text-white ${
                            loading
                                ? "cursor-not-allowed bg-gray-400"
                                : "bg-indigo-600 hover:bg-indigo-700"
                        }`}
                    >
                        {loading ? "Carving..." : "Carve Runestone"}
                    </button>
                </div>
            </div>

            <MissingPaintModal
                isOpen={isMissingPaintOpen}
                onClose={() => setIsMissingPaintOpen(false)}
            />
            <ConfirmCarveModal
                isOpen={isConfirmOpen}
                onClose={() => setIsConfirmOpen(false)}
                onConfirm={handleConfirm}
            />
        </div>
    );
};

export default RunestoneEditor;
